// Profile.js
import React, { useState, useEffect, useRef } from 'react';
import useAuth from '../hooks/useAuth';
import { doc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile as updateFirebaseProfile } from 'firebase/auth';
import { db, storage } from '../firebaseConfig';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { QRCodeSVG } from 'qrcode.react';
import './Profile.css';

const Profile = () => {
  const { user, userData, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);

  // Robust date formatting function
  const formatDate = (dateVal) => {
    if (!dateVal) return 'N/A';
    
    // Check if it's a Firestore Timestamp (has toDate function)
    if (typeof dateVal.toDate === 'function') {
      return dateVal.toDate().toLocaleDateString();
    }
    
    // Check if it's a serialized Timestamp object (has seconds property)
    if (typeof dateVal === 'object' && dateVal.seconds !== undefined) {
      return new Date(dateVal.seconds * 1000).toLocaleDateString();
    }
    
    // Parse as standard date
    const date = new Date(dateVal);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString();
    }
    
    return 'N/A';
  };
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state for editable fields
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    phone: '',
    workplace: '',
    designation: '',
    specialty: '',
    country: ''
  });

  // Real-time listener for user data updates
  useEffect(() => {
    if (!user?.uid) return;

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Update form data when Firestore data changes
        setFormData({
          title: data.title || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phone: data.phone || '',
          workplace: data.workplace || '',
          designation: data.designation || '',
          specialty: data.specialty || '',
          country: data.country || ''
        });
      }
    }, (error) => {
      console.error('Error listening to user data:', error);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Initialize form data from userData
  useEffect(() => {
    if (userData) {
      setFormData({
        title: userData.title || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
        workplace: userData.workplace || '',
        designation: userData.designation || '',
        specialty: userData.specialty || '',
        country: userData.country || ''
      });
    }
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Update Firestore user data
      await updateProfile(formData);
      
      // Update display name if first/last name changed
      const fullName = `${formData.title} ${formData.firstName} ${formData.lastName}`.trim();
      if (fullName && fullName !== user.displayName) {
        await updateFirebaseProfile(user, {
          displayName: fullName
        });
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file.' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 5MB.' });
      return;
    }

    setUploadingAvatar(true);
    setMessage({ type: '', text: '' });
    setImageError(false); // Reset image error state

    try {
      // Create a reference to the storage location
      const storageRef = ref(storage, `avatars/${user.uid}/${Date.now()}_${file.name}`);
      
      // Upload the file with metadata to prevent caching issues
      const metadata = {
        contentType: file.type,
        cacheControl: 'public, max-age=31536000',
        customMetadata: {
          uploadedBy: user.uid,
          uploadedAt: new Date().toISOString()
        }
      };
      
      await uploadBytes(storageRef, file, metadata);
      
      // Get the download URL with a cache-busting parameter
      let photoURL = await getDownloadURL(storageRef);
      
      // Update Firebase Auth profile
      await updateFirebaseProfile(user, { photoURL });
      
      // Update Firestore user document
      await updateProfile({ photoURL });
      
      setMessage({ type: 'success', text: 'Avatar updated successfully!' });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setMessage({ type: 'error', text: 'Failed to upload avatar. Please try again.' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to current userData
    if (userData) {
      setFormData({
        title: userData.title || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
        workplace: userData.workplace || '',
        designation: userData.designation || '',
        specialty: userData.specialty || '',
        country: userData.country || ''
      });
    }
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  if (!user || !userData) {
    return (
      <div className="page profile-page">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  const fullName = `${userData.title || ''} ${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'N/A';
  const avatarUrl = user.photoURL || userData.photoURL || null;
  // Use cache-busted URL to avoid 412 errors
  const displayAvatarUrl = avatarUrl ? `${avatarUrl}${avatarUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : null;

  return (
    <div className="page profile-page">
      <div className="profile-container">
        <h1>My Profile</h1>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="profile-content">
          {/* Left Column - Avatar and Membership */}
          <div className="profile-sidebar">
            {/* Avatar Section */}
            <div className="avatar-section">
              <div className="avatar-wrapper" onClick={handleAvatarClick}>
                {uploadingAvatar ? (
                  <div className="avatar-loading">
                    <div className="spinner"></div>
                  </div>
                ) : displayAvatarUrl && !imageError ? (
                  <img 
                    src={displayAvatarUrl} 
                    alt="Profile" 
                    className="avatar-image"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="avatar-initials">
                      {userData.firstName?.[0] || 'U'}{userData.lastName?.[0] || ''}
                    </span>
                  </div>
                )}
                <div className="avatar-overlay">
                  <span>Change Photo</span>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                style={{ display: 'none' }}
              />
              <p className="avatar-hint">Click to upload new photo</p>
            </div>

            {/* Membership Card */}
            <div className="membership-section">
              <h3>Membership Information</h3>
              <div className="membership-id-card">
                <div className="membership-id">
                  <label>Membership ID</label>
                  <span className="id-number">{userData.membershipID || 'Not Assigned'}</span>
                </div>
                <div className="membership-status">
                  <label>Status</label>
                  <span className={`status-badge ${userData.isActive !== false ? 'active' : 'inactive'}`}>
                    {userData.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                {/* QR Code for Business Card */}
                <div className="membership-qr-section">
                  <label>Digital Business Card</label>
                  <div className="qr-code-container">
                    <QRCodeSVG
                      value={`${window.location.origin}/member-card/${user.uid}`}
                      size={150}
                      level="H"
                    />
                  </div>
                  <p className="qr-description">
                    Scan to view digital business card
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Profile Information */}
          <div className="profile-main">
            <div className="profile-header">
              <h2>{fullName}</h2>
              {!isEditing && (
                <Button onClick={() => setIsEditing(true)} variant="primary">
                  Edit Profile
                </Button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="profile-form">
              {/* Personal Information */}
              <section className="form-section">
                <h3>Personal Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Email</label>
                    <Input
                      type="email"
                      value={userData.email || ''}
                      disabled
                      className="disabled-field"
                    />
                  </div>

                  <div className="form-group">
                    <label>Title</label>
                    <Input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="e.g., Dr., Prof."
                    />
                  </div>

                  <div className="form-group">
                    <label>First Name</label>
                    <Input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="First Name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Name</label>
                    <Input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Last Name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Phone Number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Country</label>
                    <Input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Country"
                    />
                  </div>
                </div>
              </section>

              {/* Professional Information */}
              <section className="form-section">
                <h3>Professional Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Workplace</label>
                    <Input
                      type="text"
                      name="workplace"
                      value={formData.workplace}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Hospital/Clinic Name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Designation</label>
                    <Input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Job Title"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Specialty</label>
                    <Input
                      type="text"
                      name="specialty"
                      value={formData.specialty}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Medical Specialty"
                    />
                  </div>
                </div>
              </section>

              {/* Account Information */}
              <section className="form-section">
                <h3>Account Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Role</label>
                    <span className="role-badge">{userData.role || 'member'}</span>
                  </div>
                  <div className="info-item">
                    <label>Member Since</label>
                    <span>{formatDate(userData.createdAt)}</span>
                  </div>
                  <div className="info-item">
                    <label>Last Updated</label>
                    <span>{formatDate(userData.updatedAt)}</span>
                  </div>
                </div>
              </section>

              {/* Action Buttons */}
              {isEditing && (
                <div className="form-actions">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleCancel} disabled={loading}>
                    Cancel
                  </Button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
